import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Product } from "@/lib/types";
import { apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { InlineAlert } from "@/components/admin/feedback";

export function ProductStatusDialog({
  product,
  open,
  busy,
  error,
  onOpenChange,
  onSubmit,
}: {
  product: Product | undefined;
  open: boolean;
  busy: boolean;
  error: unknown;
  onOpenChange: (open: boolean) => void;
  onSubmit: (status: "active" | "disabled", reason: string) => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const nextStatus = product?.status === "active" ? "disabled" : "active";

  useEffect(() => {
    if (!open) setReason("");
  }, [open]);

  if (!product) return null;

  const disabling = nextStatus === "disabled";
  const actionLabel = disabling
    ? t("products.status.disable", "Disable Product")
    : t("products.status.enable", "Enable Product");

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !busy && onOpenChange(nextOpen)}>
      <DialogContent className={disabling ? "border-danger/35 sm:max-w-md" : "sm:max-w-md"}>
        <DialogHeader>
          <DialogTitle className={disabling ? "text-base font-semibold text-danger" : "text-base font-semibold text-ink"}>
            {actionLabel}
          </DialogTitle>
          <DialogDescription className="mt-2 text-xs leading-5 text-muted">
            {disabling
              ? t(
                  "products.status.disableDescription",
                  "New online runtime calls will be blocked. Existing Signed License Tokens keep their current offline expiry semantics.",
                )
              : t(
                  "products.status.enableDescription",
                  "New online runtime calls may resume after the server verifies the Product binding.",
                )}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4 space-y-2">
          <Label htmlFor="product-status-reason" className="text-xs">
            {t("products.status.reason", "Reason for status change")} <span className="text-danger">*</span>
          </Label>
          <Textarea
            id="product-status-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            rows={3}
            disabled={busy}
            autoFocus
          />
        </div>
        {error ? (
          <div className="mt-4">
            <InlineAlert
              title={t("products.status.updateFailed", "Product status was not updated")}
              message={apiErrorMessage(error)}
            />
          </div>
        ) : null}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            variant={disabling ? "danger" : "primary"}
            disabled={!reason.trim() || busy}
            onClick={() => onSubmit(nextStatus, reason.trim())}
          >
            {busy ? t("common.saving", "Saving…") : actionLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
